import React, { useState, useContext } from 'react';
import { oneOf } from 'prop-types';
import useStyles from 'hooks/useStyles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ApplicationContext from 'components/ApplicationContext';
import SeeMoreRow from 'components/SeeMoreRow';

import {
  getInstructor,
  getInstructorContentList,
} from 'reduxModules/instructorProfile/selector';
import ContentIntroCard from 'components/ContentIntroCard';
import { COURSE_TYPE, REGULAR_CLASS_TYPE } from 'constants/routingPath';
import {
  COURSE_BY_INSTRUCTOR,
  REGULAR_CLASS_BY_INSTRUCTOR,
} from 'constants/eventSection';
import { INSTRUCTOR } from 'constants/pageTypes';
import useGetCurrentImageType from 'hooks/useGetCurrentImageType';

import styles from './ContentByInstructor.scss';

const CONTENT_PER_SLICE = 12;

function ContentByInstructor({ contentType }) {
  useStyles(styles);

  const {
    screenWidth: { isMobile },
  } = useContext(ApplicationContext);
  const { imageType, setContainerRef } = useGetCurrentImageType();

  const instructorContentList = useSelector(getInstructorContentList);
  const [contentSliceNum, setContentSliceNum] = useState(CONTENT_PER_SLICE);

  const slicedContent = [...instructorContentList].splice(0, contentSliceNum);

  const onSeeMoreClick = () =>
    setContentSliceNum(prev => prev + CONTENT_PER_SLICE);

  const { t } = useTranslation(['instructorProfile']);
  const { id, name, avatarUrl } = useSelector(getInstructor);

  const showSeeMore = slicedContent.length !== instructorContentList.length;

  const CONTENT = {
    [COURSE_TYPE]: {
      TITLE: {
        ORIGIN_KEY: 'instructorProfile.moreCourse',
        MOBILE_KEY: 'instructorProfile.courseListTitle',
      },
    },
    [REGULAR_CLASS_TYPE]: {
      TITLE: {
        ORIGIN_KEY: 'instructorProfile.moreRegularClass',
        MOBILE_KEY: 'instructorProfile.regularClassListTitle',
      },
    },
  };

  const sectionNameMapping = {
    [COURSE_TYPE]: COURSE_BY_INSTRUCTOR,
    [REGULAR_CLASS_TYPE]: REGULAR_CLASS_BY_INSTRUCTOR,
  };

  const originTitle = t(CONTENT[contentType].TITLE.ORIGIN_KEY, {
    instructor: name,
  });
  const mobileTitle = (
    <>
      {t(CONTENT[contentType].TITLE.MOBILE_KEY)}
      <div className={styles.instructor}>
        <img className={styles.avatar} src={avatarUrl} alt={name} />
        <h5>{name}</h5>
      </div>
    </>
  );

  return (
    <>
      <h3 className={styles.title}>{isMobile ? mobileTitle : originTitle}</h3>
      <div ref={setContainerRef} className={styles.contentWrapper}>
        {slicedContent.map((contentInfo, index) => (
          <ContentIntroCard
            {...contentInfo}
            key={contentInfo.id}
            section={sectionNameMapping[contentType]}
            pageType={INSTRUCTOR}
            serialNumber={index + 1}
            instructor={{ id, name }}
            pictureUrl={contentInfo.picture[imageType]}
          />
        ))}
      </div>
      {showSeeMore && (
        <SeeMoreRow onClick={onSeeMoreClick} className={styles.seeMore} />
      )}
    </>
  );
}

ContentByInstructor.propTypes = {
  contentType: oneOf([COURSE_TYPE, REGULAR_CLASS_TYPE]).isRequired,
};

export default ContentByInstructor;
